// Country Configuration editor (F1b). Governed authoring of a country config version:
// edit as a draft, submit, then a second platform admin publishes (maker-checker + future
// effective date, enforced by the backend). Scalars are structured; the nested configs
// (identifiers, org types, payment methods, address schema, docs) are edited as JSON for
// this expert surface. See docs/country-configuration-and-currency.md.
import React, { useEffect, useState } from 'react';
import {
  Drawer, Button, Space, Input, Select, DatePicker, Typography, Alert, Divider, message, Popconfirm, Tag,
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
  getConfigById, getCountry, createConfigDraft, updateConfigDraft, submitConfig,
  publishConfig, rejectConfig, discardConfig, type ConfigContent,
} from '../../services/countryConfigService';

const { Text, Title } = Typography;
const errMsg = (e: any, f: string) => e?.response?.data?.message || f;

type Mode = { kind: 'create'; country: string; cloneFromCode?: string } | { kind: 'edit'; id: string };
interface Props { mode: Mode | null; canPublish: boolean; onClose: () => void; onSaved: () => void }

// JSON textarea field (string state; parsed on save).
const JsonField: React.FC<{ label: string; value: string; onChange: (v: string) => void; disabled?: boolean }> = ({ label, value, onChange, disabled }) => (
  <div style={{ marginBottom: 12 }}>
    <Text strong>{label}</Text>
    <Input.TextArea rows={3} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }} />
  </div>
);

const j = (v: any) => JSON.stringify(v ?? (Array.isArray(v) ? [] : {}), null, 0);

export const CountryConfigEditor: React.FC<Props> = ({ mode, canPublish, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState<string | null>(null);
  const [status, setStatus] = useState('draft');
  const [countryCode, setCountryCode] = useState('');
  const [name, setName] = useState('');
  const [defCur, setDefCur] = useState('');
  const [supported, setSupported] = useState<string[]>([]);
  const [locale, setLocale] = useState('');
  const [timezone, setTimezone] = useState('');
  const [packCountry, setPackCountry] = useState('');
  const [json, setJson] = useState<Record<string, string>>({});
  const [effective, setEffective] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (!mode) return;
    setEffective(null);
    void (async () => {
      setLoading(true);
      try {
        const src = mode.kind === 'edit'
          ? await getConfigById(mode.id)
          : mode.cloneFromCode ? await getCountry(mode.cloneFromCode) : null;
        setId(mode.kind === 'edit' ? mode.id : null);
        setStatus(mode.kind === 'edit' ? (src as any)?.status ?? 'draft' : 'draft');
        setCountryCode(mode.kind === 'create' ? mode.country : (src as any)?.country_code ?? '');
        setName(src?.country_name ?? `${mode.kind === 'create' ? mode.country : ''}`);
        setDefCur(src?.default_currency ?? '');
        setSupported(src?.supported_currencies ?? []);
        setLocale(src?.locale ?? 'en');
        setTimezone(src?.timezone ?? '');
        setPackCountry(src?.payroll_pack_country ?? (mode.kind === 'create' ? mode.country : ''));
        setJson({
          tax_identifier: j(src?.tax_identifier), company_identifier: j((src as any)?.company_identifier),
          vat_identifier: j((src as any)?.vat_identifier), organization_types: j(src?.organization_types),
          address_schema: j(src?.address_schema), required_documents: j(src?.required_documents),
          payment_methods: j(src?.payment_methods), banking_requirements: j((src as any)?.banking_requirements),
          mobile_money_providers: j(src?.mobile_money_providers),
        });
      } catch (e) {
        message.error(errMsg(e, 'Could not load config'));
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  if (!mode) return null;
  const editable = status === 'draft';

  const buildContent = (): ConfigContent | null => {
    const parsed: Record<string, any> = {};
    for (const k of Object.keys(json)) {
      const raw = json[k]?.trim();
      if (!raw) { parsed[k] = undefined; continue; }
      try { parsed[k] = JSON.parse(raw); }
      catch { message.error(`${k} is not valid JSON`); return null; }
    }
    return {
      country_name: name, default_currency: defCur, supported_currencies: supported,
      locale: locale || null, timezone: timezone || null, payroll_pack_country: packCountry || null,
      ...parsed,
    } as ConfigContent;
  };

  const save = async (): Promise<string | null> => {
    const content = buildContent();
    if (!content) return null;
    setSaving(true);
    try {
      if (!id) {
        const { id: newId } = await createConfigDraft({ country_code: countryCode, ...content });
        setId(newId); message.success('Draft created'); return newId;
      }
      await updateConfigDraft(id, content); message.success('Draft saved'); return id;
    } catch (e) { message.error(errMsg(e, 'Could not save')); return null; }
    finally { setSaving(false); }
  };

  const act = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); message.success(ok); onSaved(); }
    catch (e) { message.error(errMsg(e, 'Action failed')); }
  };

  return (
    <Drawer
      title={mode.kind === 'create' ? `New ${countryCode} config version` : `Edit config · ${status}`}
      width={780} open={!!mode} onClose={onClose} loading={loading}
      extra={editable && <Button onClick={() => void save()} loading={saving}>Save draft</Button>}
    >
      {!editable && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`This config is ${status} and can no longer be edited.`}
          description={status === 'in_review' ? 'A second operator (publisher) approves or rejects it.' : undefined} />
      )}

      <Title level={5}>Basics</Title>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <div><Text>Country name</Text><Input value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} /></div>
        <Space>
          <div><Text>Default currency</Text><Input style={{ width: 120 }} value={defCur} disabled={!editable} onChange={(e) => setDefCur(e.target.value.toUpperCase())} /></div>
          <div style={{ minWidth: 320 }}><Text>Supported currencies</Text>
            <Select mode="tags" style={{ width: '100%' }} value={supported} disabled={!editable}
              onChange={(v) => setSupported(v.map((x) => x.toUpperCase()))} tokenSeparators={[',']} /></div>
        </Space>
        <Space>
          <div><Text>Locale</Text><Input style={{ width: 100 }} value={locale} disabled={!editable} onChange={(e) => setLocale(e.target.value)} /></div>
          <div><Text>Timezone</Text><Input style={{ width: 200 }} value={timezone} disabled={!editable} onChange={(e) => setTimezone(e.target.value)} /></div>
          <div><Text>Payroll pack</Text><Input style={{ width: 90 }} value={packCountry} disabled={!editable} onChange={(e) => setPackCountry(e.target.value.toUpperCase())} /></div>
        </Space>
      </Space>

      <Divider />
      <Title level={5}>Configuration (JSON)</Title>
      <JsonField label="Tax identifier" value={json.tax_identifier || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, tax_identifier: v }))} />
      <JsonField label="Company identifier" value={json.company_identifier || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, company_identifier: v }))} />
      <JsonField label="VAT identifier" value={json.vat_identifier || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, vat_identifier: v }))} />
      <JsonField label="Organization types" value={json.organization_types || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, organization_types: v }))} />
      <JsonField label="Address schema" value={json.address_schema || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, address_schema: v }))} />
      <JsonField label="Required documents" value={json.required_documents || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, required_documents: v }))} />
      <JsonField label="Payment methods" value={json.payment_methods || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, payment_methods: v }))} />
      <JsonField label="Mobile-money providers" value={json.mobile_money_providers || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, mobile_money_providers: v }))} />
      <JsonField label="Banking requirements" value={json.banking_requirements || ''} disabled={!editable} onChange={(v) => setJson((s) => ({ ...s, banking_requirements: v }))} />

      <Divider />
      <Space wrap>
        {status === 'draft' && (
          <>
            <Button type="primary" onClick={() => void act(async () => { const pid = await save(); if (pid) await submitConfig(pid); }, 'Submitted for review')}>Submit for review</Button>
            <Popconfirm title="Discard this draft?" onConfirm={() => id && void act(() => discardConfig(id), 'Discarded')}>
              <Button danger disabled={!id}>Discard</Button>
            </Popconfirm>
          </>
        )}
        {status === 'in_review' && canPublish && (
          <>
            <DatePicker placeholder="Effective from (future)" value={effective} onChange={setEffective} />
            <Popconfirm title="Publish this country config?" description="Activates the new version for this country on the effective date."
              onConfirm={() => id && effective && void act(() => publishConfig(id, effective.format('YYYY-MM-DD')), 'Published')}>
              <Button type="primary" disabled={!effective}>Publish</Button>
            </Popconfirm>
            <Button onClick={() => id && void act(() => rejectConfig(id), 'Sent back')}>Send back</Button>
          </>
        )}
        {status === 'in_review' && !canPublish && <Tag>Awaiting a publisher (platform admin)</Tag>}
      </Space>
    </Drawer>
  );
};

export default CountryConfigEditor;
